import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useDispatch } from 'react-redux';
import {
  AUTO_UPDATE_STATUSES,
  setReviewCreationKey,
  setReviewCreationReviewerSetting,
  useReviewCreationV1Data,
} from 'reducers/reviewCreationV1Slice';
import { checkSimplifiedReviewCreationEnabled } from 'utils/helpers/featureFlags';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { useInvalidateCalibrationAndDeliverables } from './calibrationAndDeliverablesSectionHooks';
import { useInvalidateTimelineRemindersPanelData } from './timelineAndRemindersSectionHooks';

const getReviewCycleForms = ({ reviewCycleId }) =>
  sendRequest({
    url: endpoints.reviewCreationV2.forms,
    credentials: 'include',
    queryParams: {
      reviewCycleId,
    },
  }).then((res) => res.entity);

export const useGetReviewForms = ({ reviewCycleId, enabled = true }) => {
  const dispatch = useDispatch();

  return useQuery(
    [REACT_QUERY_KEYS.reviewCreationV2.forms, reviewCycleId],
    () =>
      getReviewCycleForms({ reviewCycleId }).then((data) => {
        dispatch(
          setReviewCreationKey({
            enabledProviderTypes: data.enabledProviderTypes,
            providerTypeToTemplateMap: data.providerTypeToTemplateMap,
          })
        );
        return data;
      }),
    { enabled: enabled && !!reviewCycleId && checkSimplifiedReviewCreationEnabled() }
  );
};

const updateFormsProvidersApi = ({ reviewCycleId, enabledProviderTypes }) =>
  sendRequest({
    url: endpoints.reviewCreationV2.providerTypes,
    method: 'PUT',
    body: {
      reviewCycleId,
      enabledProviderTypes,
    },
    credentials: 'include',
  }).then((res) => res.entity);

export const useUpdateEnabledProviderTypes = () => {
  const { reviewCycleId } = useReviewCreationV1Data();
  const invalidateCalibrationAndDeliverables = useInvalidateCalibrationAndDeliverables();
  const invalidateTimelineReminders = useInvalidateTimelineRemindersPanelData();

  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ enabledProviderTypes }) =>
      updateFormsProvidersApi({ reviewCycleId, enabledProviderTypes }).then((data) => {
        dispatch(
          setReviewCreationKey({
            enabledProviderTypes: data.enabledProviderTypes,
            providerTypeToTemplateMap: data.providerTypeToTemplateMap,
          })
        );
        return data;
      }),
    onMutate: () => {
      dispatch(setReviewCreationKey({ autoUpdateStatus: AUTO_UPDATE_STATUSES.IS_SAVING }));
    },
    onSuccess: (data) => {
      invalidateCalibrationAndDeliverables();
      invalidateTimelineReminders();
      setTimeout(() => dispatch(setReviewCreationKey({ autoUpdateStatus: AUTO_UPDATE_STATUSES.UPDATED })), 500);
    },
    onError: (data) => {
      dispatch(setReviewCreationKey({ autoUpdateStatus: AUTO_UPDATE_STATUSES.IS_ERROR }));
    },
  });
};

const updateFormsTemplatesApi = ({ reviewCycleId, providerTypeToTemplateMap }) => {
  const providerTypeToTemplateMap1 = {};
  Object.keys(providerTypeToTemplateMap).forEach((key) => {
    providerTypeToTemplateMap1[key] = { templateId: providerTypeToTemplateMap[key]?.templateId };
  });

  return sendRequest({
    url: endpoints.reviewCreationV2.templates,
    method: 'PUT',
    body: {
      reviewCycleId,
      providerTypeToTemplateMap: providerTypeToTemplateMap1,
    },
    credentials: 'include',
  }).then((res) => res.entity);
};

export const useUpdateFormTemplates = () => {
  const { reviewCycleId } = useReviewCreationV1Data();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ providerTypeToTemplateMap }) =>
      updateFormsTemplatesApi({ reviewCycleId, providerTypeToTemplateMap }).then((data) => {
        dispatch(
          setReviewCreationKey({
            enabledProviderTypes: data.enabledProviderTypes,
            providerTypeToTemplateMap: data.providerTypeToTemplateMap,
          })
        );
        return data;
      }),

    onMutate: () => {
      dispatch(setReviewCreationKey({ autoUpdateStatus: AUTO_UPDATE_STATUSES.IS_SAVING }));
    },
    onSuccess: (data) => {
      setTimeout(() => dispatch(setReviewCreationKey({ autoUpdateStatus: AUTO_UPDATE_STATUSES.UPDATED })), 500);
    },
    onError: (data) => {
      dispatch(setReviewCreationKey({ autoUpdateStatus: AUTO_UPDATE_STATUSES.IS_ERROR }));
    },
  });
};

const getFormPanelData = ({ reviewCycleId }) =>
  sendRequest({
    url: endpoints.reviewCreationV2.formsPanel,
    credentials: 'include',
    queryParams: {
      reviewCycleId,
    },
  }).then((res) => res.entity);

export const useGetFormsPanelData = () => {
  const dispatch = useDispatch();
  const { reviewCycleId } = useReviewCreationV1Data();

  return useQuery(
    [REACT_QUERY_KEYS.reviewCreationV2.formsPanel, reviewCycleId],
    () =>
      getFormPanelData({ reviewCycleId }).then((data) => {
        dispatch(setReviewCreationReviewerSetting(data));
        return data;
      }),
    { enabled: !!reviewCycleId }
  );
};

const updateFormsPanelData = ({ reviewCycleId, data }) =>
  sendRequest({
    url: endpoints.reviewCreationV2.formsPanel,
    method: 'PUT',
    body: { reviewCycleId, ...data },
    credentials: 'include',
  }).then((res) => res.entity);

export const useUpdateFormsPanelData = () => {
  const { reviewerSettings, reviewCycleId } = useReviewCreationV1Data();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: () => updateFormsPanelData({ reviewCycleId, data: reviewerSettings }),

    onSuccess: (data) => {
      dispatch(setReviewCreationKey({ isFormsSettingsPanelOpen: false }));
    },
    onError: (data) => {},
  });
};

// ----------------------  Invalidators -------------------------------------

export const useInvalidateFormsPanelData = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEYS.reviewCreationV2.formsPanel],
    });
};
